import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getServiceRequests, createServiceRequest } from '../api/admin.js';
import type {
  ServiceRequest,
  ServiceRequestCategory,
  ServiceRequestState,
} from '@m8-app/contracts/rest/admin.js';

const STATES: ServiceRequestState[] = [
  'open', 'in_review', 'repair_scheduled', 'replace_scheduled',
  'resolved', 'rejected', 'closed',
];

export function Services() {
  const [cursor, setCursor] = useState<string | null>(null);
  const [state, setState] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const params = { ...(cursor ? { cursor } : {}), ...(state ? { state } : {}) };
  const { data, isLoading, isError } = useQuery({
    queryKey: ['serviceRequests', params],
    queryFn: () => getServiceRequests(params),
  });

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Service Management</h2>
          <p className="text-sm text-gray-500">Customer-raised and staff-raised tickets. Action as repair or replace.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700">
          + New Service Request
        </button>
      </div>

      <div className="flex gap-3 mb-4">
        <select value={state} onChange={(e) => { setState(e.target.value); setCursor(null); }} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
          <option value="">All states</option>
          {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Device</th>
              <th className="px-4 py-3 font-medium">Raised by</th>
              <th className="px-4 py-3 font-medium">Category</th>
              <th className="px-4 py-3 font-medium">Description</th>
              <th className="px-4 py-3 font-medium">State</th>
              <th className="px-4 py-3 font-medium">Resolution</th>
              <th className="px-4 py-3 font-medium">Remark</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isError ? (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-red-600">Couldn't load service requests. Check your connection and try again.</td></tr>
            ) : (
              <>
                {data?.requests.map((r) => <Row key={r.id} r={r} />)}
                {data && data.requests.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">No service requests</td></tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {data?.nextCursor && (
        <button onClick={() => setCursor(data.nextCursor)} className="mt-3 text-sm text-indigo-600 hover:underline">Load more</button>
      )}

      {showAdd && <AddDialog onClose={() => setShowAdd(false)} />}
    </div>
  );
}

function Row({ r }: { r: ServiceRequest }) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 font-mono text-xs">{r.serialNumber}</td>
      <td className="px-4 py-3">{r.raisedBy}</td>
      <td className="px-4 py-3">{r.category}</td>
      <td className="px-4 py-3 max-w-xs truncate">{r.description}</td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{r.state}</span>
      </td>
      <td className="px-4 py-3">{r.resolution ?? '—'}</td>
      <td className="px-4 py-3 max-w-[180px] truncate">{r.remark || <span className="italic text-gray-400 text-xs">—</span>}</td>
      <td className="px-4 py-3 text-gray-400">{r.createdAt.slice(0, 10)}</td>
      <td className="px-4 py-3"><Link to={`/services/${r.id}`} className="text-xs text-indigo-600 hover:underline">Open</Link></td>
    </tr>
  );
}

function AddDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [serialNumber, setSerialNumber] = useState('');
  const [category, setCategory] = useState<ServiceRequestCategory>('hardware');
  const [description, setDescription] = useState('');
  const [err, setErr] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () => createServiceRequest({ serialNumber, category, description }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['serviceRequests'] });
      onClose();
    },
    onError: (e: unknown) => setErr((e as { message?: string }).message ?? 'Create failed'),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    mutate();
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">New Service Request</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label htmlFor="sr-serial" className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
            <input
              id="sr-serial"
              type="text"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g. CEIL-2026-1"
              required
            />
          </div>
          <div>
            <label htmlFor="sr-category" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              id="sr-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as ServiceRequestCategory)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {(['hardware','installation','connectivity','config','other'] as ServiceRequestCategory[]).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="sr-description" className="block text-sm font-medium text-gray-700 mb-1">Problem Description</label>
            <textarea
              id="sr-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Describe the issue in detail…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>
          {err && <p className="text-sm text-red-600">{err}</p>}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={isPending} className="flex-1 bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              {isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
